// Enums
export enum UserRole {
  ADMIN = 'ADMIN',
  VENDEDORA = 'VENDEDORA',
  VENDEDORA_EN_LINEA = 'VENDEDORA_EN_LINEA',
  MODISTA = 'MODISTA'
}

export enum ComisionTipo {
  VENTA = 'VENTA',
  TICKET = 'TICKET'
}

export enum ComisionEstado {
  PENDIENTE = 'PENDIENTE',
  PAGADA = 'PAGADA',
  CANCELADA = 'CANCELADA'
}

// Privilegios del sistema
export interface Privilegios {
  ventas: boolean;
  productos: boolean;
  tickets: boolean;
  costuras: boolean;
  pagos: boolean;
  usuarios: boolean;
  almacenes: boolean;
  proveedores: boolean;
  categorias: boolean;
  calendario: boolean;
  reportes: boolean;
}

// User types
export interface User {
  id_usuario: string;
  username: string;
  email: string | null;
  pass_usr?: string; // Should be optional as it might not be returned always or useful for all
  rol_usr: UserRole;
  estado: boolean | null;
  nombre_completo: string | null;
  telefono: string | null;
  porcentaje_comision: number;
  created_at: string;
  firebaseUid: string | null;
  codigo_vendedora?: string | null;
  privilegios?: Privilegios;
  _count?: {
    sales: number;
    tickets_tickets_assignedToIdToUsuario: number;
    comisiones?: number;
  };
}

export interface CreateUserDTO {
  username: string;
  email: string;
  password: string;
  rol_usr: UserRole;
  nombre_completo?: string;
  telefono?: string;
  porcentaje_comision?: number;
  codigo_vendedora?: string;
  privilegios?: Privilegios;
}

export interface UpdateUserDTO {
  username?: string;
  email?: string;
  password?: string; // Add password for updates
  rol_usr?: UserRole;
  nombre_completo?: string;
  telefono?: string;
  porcentaje_comision?: number;
  codigo_vendedora?: string;
  estado?: boolean;
  privilegios?: Privilegios;
}

export interface UserStats {
  user: {
    id_usuario: string;
    username: string;
    nombre_completo: string | null;
    rol_usr: UserRole;
    porcentaje_comision: number;
  };
  stats: {
    total_ventas: number;
    monto_total_ventas: number;
    total_tickets_asignados: number;
    tickets_completados: number;
    comisiones_pendientes: number;
    comisiones_pagadas: number;
    monto_comisiones_pendientes: number;
    monto_comisiones_pagadas: number;
  };
}

// Auth types
export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Comision types
export interface Comision {
  id: number;
  usuario_id: string;
  tipo: ComisionTipo;
  monto: number;
  porcentaje: number;
  estado: ComisionEstado;
  fecha_calculo: string;
  fecha_pago: string | null;
  periodo_inicio: string;
  periodo_fin: string;
  notas: string | null;
  venta_id: number | null;
  ticket_id: number | null;
  Usuario?: {
    id_usuario: string;
    username: string;
    nombre_completo: string | null;
    email: string | null;
    rol_usr: UserRole;
  };
  sales?: {
    id: number;
    folio: string;
    customerName: string;
  };
  tickets?: {
    id: number;
    title: string;
  } | null;
  payment?: {
    id: number;
    amount: number;
    method: string;
  };
  prendas?: Array<{
    marca: string;
    modelo: string;
    talla: string;
    color: string;
    precio: number;
    tipo: string;
  }>;
}

export interface VendedoraStats {
  usuario_id: string;
  nombre: string;
  email: string | null;
  total_ventas: number;
  ventas_completadas?: number;
  monto_total_ventas: number;
  monto_ventas_completadas?: number;
  porcentaje_comision: number;
  comision_calculada: number;
  ventas_por_mes: Array<{
    mes: string;
    cantidad: number;
    monto: number;
  }>;
}

export interface ModistaStats {
  usuario_id: string;
  nombre: string;
  email: string | null;
  total_tickets: number;
  tickets_completados: number;
  porcentaje_comision: number;
  comision_calculada: number;
  tickets_por_mes: Array<{
    mes: string;
    cantidad: number;
  }>;
}

export interface ComisionResumen {
  usuario: {
    id_usuario: string;
    nombre: string;
    rol: UserRole;
    porcentaje_comision: number;
  };
  comisiones: {
    total: number;
    pendientes: number;
    pagadas: number;
    canceladas: number;
  };
  montos: {
    total: number;
    pendiente: number;
    pagado: number;
  };
  historial: Array<{
    id: number;
    tipo: ComisionTipo;
    monto: number;
    porcentaje: number;
    estado: ComisionEstado;
    fecha_calculo: string;
    fecha_pago: string | null;
    periodo_inicio: string;
    periodo_fin: string;
  }>;
}

export interface DashboardPagos {
  periodo: {
    inicio: string;
    fin: string;
  };
  totales: {
    comisiones: number;
    monto_total: number;
    pendientes: number;
    monto_pendiente: number;
    pagadas: number;
    monto_pagado: number;
  };
  por_tipo: {
    ventas: {
      cantidad: number;
      monto: number;
    };
    tickets: {
      cantidad: number;
      monto: number;
    };
  };
  comisiones: Comision[];
}

// Permissions
export interface RolePermissions {
  [key: string]: string[];
}

export const rolePermissions: RolePermissions = {
  [UserRole.ADMIN]: [
    'users:read',
    'users:create',
    'users:update',
    'users:delete',
    'sales:read',
    'sales:create',
    'sales:update',
    'sales:delete',
    'products:read',
    'products:create',
    'products:update',
    'products:delete',
    'tickets:read',
    'tickets:create',
    'tickets:update',
    'tickets:delete',
    'tickets:assign',
    'dashboard:read',
    'payments:read',
    'payments:manage',
    'calendar:read',
    'calendar:manage',
    'reports:read',
    'reports:export',
    'clients:read',
    'clients:create',
    'clients:update',
    'clients:delete'
  ],
  [UserRole.VENDEDORA]: [
    'sales:read',
    'sales:create',
    'products:read',
    'tickets:read',
    'tickets:create',
    'calendar:read',
    'clients:read',
    'clients:create',
    'clients:update'
  ],
  [UserRole.VENDEDORA_EN_LINEA]: [
    'sales:read',
    'sales:create',
    'products:read',
    'tickets:read',
    'tickets:create',
    'calendar:read',
    'clients:read',
    'clients:create',
    'clients:update'
  ],
  [UserRole.MODISTA]: [
    'tickets:read',
    'tickets:update',
    'calendar:read'
  ]
};

export const hasPermission = (role: UserRole, permission: string): boolean => {
  return rolePermissions[role]?.includes(permission) || false;
};

export const canAccessRoute = (role: UserRole, route: string): boolean => {
  const routePermissions: Record<string, string[]> = {
    '/dashboard': ['dashboard:read'],
    '/users': ['users:read'],
    '/sales': ['sales:read'],
    '/products': ['products:read'],
    '/tickets': ['tickets:read'],
    '/payments': ['payments:read'],
    '/calendar': ['calendar:read'],
    '/clientes': ['clients:read'],
    '/reportes': ['reports:read']
  };

  const requiredPermissions = routePermissions[route] || [];
  return requiredPermissions.every(permission => hasPermission(role, permission));
};

// API Error
export interface ApiError {
  message: string;
  status: number;
  errors?: Record<string, string[]>;
}

// API Response
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

// Dashboard types
export interface DashboardMetrics {
  salesCount: number;
  totalRevenue: number;
  productsInStock: number;
  pendingTickets: number;
  completedSalesCount: number;
  pendingSalesCount: number;
  lowStockProducts: number;
  totalApartados: number;
  totalPreOrders: number;
  totalCobrado: number;
  totalPendiente: number;
  expiredApartadosCount: number;
  pendingApartadosCount: number;
  pendingPreOrdersCount: number;
  warningApartadosCount: number;
}

export interface RecentSale {
  id: string;
  folio?: string;
  customerName: string;
  products: string;
  total: number;
  status: 'PENDING' | 'COMPLETED' | 'CANCELLED';
  paymentMethod: 'CASH' | 'CARD' | 'TRANSFER';
  date: string;
  sellerName?: string;
}

export interface SalesChartData {
  date: string;
  total: number;
  count: number;
}

export interface TopProduct {
  productId: string;
  name: string;
  sku: string;
  category: string;
  quantitySold: number;
  totalRevenue: number;
  currentStock: number;
}

export interface LowStockProduct {
  id: string;
  name: string;
  sku: string;
  stock: number;
  minStock: number;
  price: number;
  category: string;
  stockPercentage: number;
}

export interface RevenueComparison {
  currentMonth: {
    revenue: number;
    salesCount: number;
  };
  lastMonth: {
    revenue: number;
    salesCount: number;
  };
  comparison: {
    revenueChange: number;
    salesChange: number;
    revenueGrowth: boolean;
    salesGrowth: boolean;
  };
}
